import { useRef, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { AvailableApp } from "@/lib/api/controlPlaneClient";
import { CanvaAppCard } from "./CanvaAppCard";

/**
 * Horizontal, scrollable row of Canva-style app cards with a title, optional
 * subtitle and prev/next scroll controls — mirrors Canva's "Top picks" shelves.
 */
export function AppShelf({
  title,
  subtitle,
  apps,
  isInstalled,
  onCardClick,
}: {
  title: string;
  subtitle?: string;
  apps: AvailableApp[];
  isInstalled: (name: string) => boolean;
  onCardClick: (app: AvailableApp) => void;
}): ReactNode {
  const scrollerRef = useRef<HTMLDivElement>(null);

  const scrollBy = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.85), behavior: "smooth" });
  };

  if (apps.length === 0) return null;

  return (
    <section className="mb-9">
      <div className="mb-3 flex items-end justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          {subtitle && (
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
          )}
        </div>
        <div className="hidden shrink-0 gap-1.5 sm:flex">
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label="Scroll left"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label="Scroll right"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="scrollbar-none -mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-1 pb-1"
      >
        {apps.map((app) => (
          <div key={app.name} className="shrink-0 snap-start">
            <CanvaAppCard
              app={app}
              isInstalled={isInstalled(app.name)}
              onClick={onCardClick}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
